// Admin panel (SPEC-391): user management + invite codes. Admin-only entry from the topbar.
// Server-side guards enforce the role; this component is a convenience surface.

import { useCallback, useEffect, useState } from 'react';
import { useT } from '../i18n';
import { adminCreateInvite, adminDeleteUser, adminListUsers, adminSetUserStatus, type AdminUser } from '../api';
import { XIcon } from './icons';

export interface AdminPanelProps {
  currentUser: string;
  onClose: () => void;
}

export default function AdminPanel({ currentUser, onClose }: AdminPanelProps) {
  const t = useT();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [newInvites, setNewInvites] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => { void adminListUsers().then(setUsers); }, []);
  useEffect(refresh, [refresh]);

  const act = async (fn: () => Promise<boolean>) => {
    if (busy) return;
    setBusy(true);
    const ok = await fn();
    setBusy(false);
    setError(ok ? null : t('admin.opFailed'));
    refresh();
  };

  const createInvite = async (): Promise<boolean> => {
    const code = await adminCreateInvite();
    if (code) { setNewInvites(v => [code, ...v]); return true; }
    return false;
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-panel" onClick={e => e.stopPropagation()}>
        <div className="admin-head">
          <h2>{t('admin.title')}</h2>
          <button className="ghost-btn icon-btn" onClick={onClose} aria-label="close"><XIcon /></button>
        </div>
        {error && <p className="login-error">{error}</p>}

        <h3 className="admin-section-title">{t('admin.invites')}</h3>
        <div className="admin-invite-row">
          <button className="primary-btn" disabled={busy} onClick={() => void act(createInvite)}>
            {t('admin.createInvite')}
          </button>
        </div>
        {newInvites.length > 0 && (
          <ul className="admin-invite-list">
            {newInvites.map(code => (
              <li key={code} className="admin-invite-item">
                <code>{code}</code>
                <button
                  className="ghost-btn"
                  onClick={() => { void navigator.clipboard?.writeText(code); }}
                >{t('admin.copy')}</button>
              </li>
            ))}
          </ul>
        )}
        <p className="hint">{t('admin.inviteHint')}</p>

        <h3 className="admin-section-title">{t('admin.users')}</h3>
        {/* SPEC-468 M1 (A-04): scroll container — on phone widths the table keeps its native
            layout (column alignment intact) and scrolls horizontally; every column/action stays
            reachable (G2). No-op when the table fits (no scrollbar). */}
        <div className="admin-table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('login.username')}</th>
              <th>{t('admin.role')}</th>
              <th>{t('admin.status')}</th>
              <th>{t('admin.lastLogin')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.username}>
                <td>{u.username}{u.username === currentUser ? ` (${t('admin.you')})` : ''}</td>
                <td>{u.role}</td>
                <td>{t(u.status === 'active' ? 'admin.statusActive' : u.status === 'pending' ? 'admin.statusPending' : 'admin.statusDisabled')}</td>
                <td>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : t('admin.never')}</td>
                <td className="admin-actions">
                  {u.username !== currentUser && (
                    <>
                      {u.status !== 'active' && (
                        <button className="ghost-btn" disabled={busy} onClick={() => void act(() => adminSetUserStatus(u.username, 'active'))}>
                          {t('admin.activate')}
                        </button>
                      )}
                      {u.status === 'active' && (
                        <button className="ghost-btn" disabled={busy} onClick={() => void act(() => adminSetUserStatus(u.username, 'disabled'))}>
                          {t('admin.disable')}
                        </button>
                      )}
                      <button className="danger-btn" disabled={busy} onClick={() => void act(() => adminDeleteUser(u.username))}>
                        {t('admin.delete')}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
